const GRAPHQL_ENDPOINT = '/api/landings/magento?path=/graphql';

export async function executeQuery(query: string, variables = {}) {
    const response = await fetch(GRAPHQL_ENDPOINT, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
            query,
            variables
        }),
    });

    if (!response.ok) {
        return null;
    }

    const result = await response.json();
    
    if (result.errors) {
        return null;
    }

    return result.data;
}

export async function createEmptyCart() {
    const query = `
        mutation {
            createEmptyCart
        }
    `;

    const result = await executeQuery(query);
    return result.createEmptyCart;
}

export type MagentoProduct = {
    sku: string;
    name: string;
    mindbox_id?: string | null;
    special_price?: number | null;
    price?: {
        maximum_price?: {
            final_price?: {
                value?: number | null;
            };
        };
    };
    stock_status: string;
    image?: {
        url: string;
    };
};

export async function getProducts(skus: string[]): Promise<MagentoProduct[]> {
    const query = `
        query GetProducts($skus: [String!]!) {
            products(filter: { sku: { in: $skus } }) {
                items {
                    sku
                    name
                    mindbox_id
                    special_price
                    price: price_range {
                        maximum_price {
                            final_price {
                                value
                                currency
                            }
                        }
                    }
                    image {
                        url
                    }
                    stock_status
                    __typename
                    ... on ConfigurableProduct {
                        variants {
                            product {
                                sku
                                name
                                mindbox_id
                                special_price
                                price: price_range {
                                    maximum_price {
                                        final_price { value currency }
                                    }
                                }
                                image { url }
                                stock_status
                            }
                        }
                    }
                }
            }
        }
    `;

    const variables = { skus };
    const result = await executeQuery(query, variables);
    const items = (result?.products?.items || []) as Array<any>;
    const flattened: Array<any> = [];

    for (const item of items) {
        flattened.push(item);
        if (item?.variants?.length) {
            for (const variant of item.variants) {
                if (variant?.product) {
                    flattened.push(variant.product);
                }
            }
        }
    }

    const requestedSkuSet = new Set(skus);
    const exactMatches = flattened.filter(p => requestedSkuSet.has(p.sku));
    const orderMap: Record<string, number> = skus.reduce((acc, sku, index) => {
        acc[sku] = index;
        return acc;
    }, {} as Record<string, number>);

    exactMatches.sort((a, b) => orderMap[a.sku] - orderMap[b.sku]);
    return exactMatches as MagentoProduct[];
}

export async function addProductToCart(cartId: string, sku: string, quantity = 1) {
    const query = `
        mutation AddProductToCart($cartId: String!, $sku: String!, $quantity: Float!) {
            addSimpleProductsToCart(
                input: {
                    cart_id: $cartId,
                    cart_items: [
                        {
                            data: {
                                quantity: $quantity,
                                sku: $sku
                            }
                        }
                    ]
                }
            ) {
                cart {
                    items {
                        product {
                            sku
                            name
                            image {
                                url
                            }
                        }
                        quantity
                    }
                }
            }
        }
    `;

    const variables = {
        cartId,
        sku,
        quantity
    };

    return executeQuery(query, variables);
}
